import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Shield, ShieldAlert, Activity, Eye, RefreshCw, Globe, Clock, Crosshair, Radar, Zap, Volume2, VolumeX, Ban, Unlock, MapPin, Server, Wifi, Search, AlertTriangle, Skull, Target, Radio, Fingerprint, ChevronDown, ChevronUp, Trash2, X, ShieldCheck, Siren, Network, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import API_URL from '../../utils/apiUrl';

const WS_URL = (typeof window !== 'undefined')
  ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
  : (API_URL ? API_URL.replace('https://', 'wss://').replace('http://', 'ws://') : '');

const CRITICAL_TYPES = new Set([
  'honeypot_trap', 'scanner_detected', 'waf_sql_injection', 'waf_xss_attempt',
  'waf_path_traversal', 'waf_command_injection', 'waf_ssrf_attempt',
  'waf_advanced_injection', 'waf_encoded_attack', 'nosql_injection', 'brute_force_block'
]);

const severityColor = (sev) => {
  const colors = { EXTREME: 'bg-fuchsia-600 text-white', CRITICAL: 'bg-red-600 text-white', HIGH: 'bg-orange-500 text-white', MEDIUM: 'bg-yellow-500 text-black', LOW: 'bg-slate-600 text-slate-200' };
  return colors[sev] || colors.LOW;
};

const severityBorder = (sev) => {
  const colors = { EXTREME: 'border-fuchsia-500/50', CRITICAL: 'border-red-500/50', HIGH: 'border-orange-500/50', MEDIUM: 'border-yellow-500/50', LOW: 'border-slate-700' };
  return colors[sev] || colors.LOW;
};

// ==================== LIVE FEED WITH AUTO-BLOCK ALERTS ====================
const LiveFeed = ({ token }) => {
  const [liveEvents, setLiveEvents] = useState([]);
  const [autoBlocks, setAutoBlocks] = useState([]);
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const wsRef = useRef(null);
  const audioRef = useRef(null);
  const sirenRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGczIjqT3/DdlVtCMW+08NWYYkA0a6vI2ql5T0Vqm8LRq39ZS2eZvcyrtmlSZ5S2x7K6clZnlbTCuL5wWGeVtMG4vnJYaJe0wLjBclpomLTAucFyWmiYtMC5wXJaaJi0wLnBcllomLXAucFyWWiYtcC4wnNZaJe1wLjCc1lol7XAuMJzWQ==');
    sirenRef.current = new Audio('data:audio/wav;base64,UklGRl4FAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YToFAACAgICAgIB/f39+fn59fX18fHx7e3t6enp5eXl4eHh4d3d3d3Z2dnZ2dnV1dXV1dXV1dXV1dXV1dnZ2dnZ2d3d3d3h4eHh5eXl6enp7e3t8fHx9fX1+fn5/f3+AgICBgYGCgoKDg4OEhISFhYWGhoaHh4eIiIiJiYmKioqLi4uMjIyNjY2Ojo6Pj4+QkJCRkZGSkpKTk5OUlJSVlZWVlZWWlpaWlpaWlpaWlpaVlZWVlZWUlJSUlJSTk5OSkpKRkZGQkJCPj4+Ojo6NjY2MjIyLi4uKioqJiYmIiIiHh4eGhoaFhYWEhISDg4OCgoKBgYGAgIB/f39+fn59fX18fHx7e3t6enp5eXl4eHg=');
    return () => { audioRef.current = null; sirenRef.current = null; };
  }, []);

  const playAlert = useCallback(() => {
    if (!soundEnabled || !audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  }, [soundEnabled]);

  const playSiren = useCallback(() => {
    if (!soundEnabled || !sirenRef.current) return;
    sirenRef.current.currentTime = 0;
    sirenRef.current.play().catch(() => {});
  }, [soundEnabled]);

  useEffect(() => {
    if (!token || !WS_URL) return;
    let reconnectTimer;
    const connect = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/ws/security?token=${token}`);
        ws.onopen = () => setConnected(true);
        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'security_event') {
            setLiveEvents(prev => [data.event, ...prev].slice(0, 100));
            if (CRITICAL_TYPES.has(data.event.event_type)) playAlert();
          }
          if (data.type === 'auto_block') {
            setAutoBlocks(prev => [data, ...prev].slice(0, 50));
            playSiren();
            toast.error(`AUTO-BLOCKED: ${data.ip} (${data.city}, ${data.country}) - Score: ${data.score}`, { duration: 10000 });
          }
        };
        ws.onclose = () => { setConnected(false); reconnectTimer = setTimeout(connect, 5000); };
        wsRef.current = ws;
      } catch {}
    };
    connect();
    return () => { if (wsRef.current) wsRef.current.close(); clearTimeout(reconnectTimer); };
  }, [token, playAlert, playSiren]);

  return (
    <div className="space-y-4">
      {/* Auto-Block Alerts */}
      {autoBlocks.length > 0 && (
        <Card className="bg-red-950/30 border-red-500/50 animate-pulse">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm font-mono flex items-center gap-2 text-red-400">
              <Siren className="w-4 h-4" /> AUTO-BLOCK ALERTS ({autoBlocks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 max-h-32 overflow-y-auto">
            {autoBlocks.map((b, i) => (
              <div key={i} className="flex items-center justify-between text-xs font-mono bg-red-950/50 rounded px-2 py-1 border border-red-500/30">
                <span className="text-red-400 font-bold">{b.ip}</span>
                <span className="text-slate-400">{b.city}, {b.country}</span>
                <Badge className="bg-red-600 text-[10px]">Score: {b.score}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Live Feed */}
      <Card className="bg-slate-950 border-slate-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-mono flex items-center gap-2">
              <Radio className={`w-4 h-4 ${connected ? 'text-green-400 animate-pulse' : 'text-red-400'}`} />
              LIVE THREAT FEED
              {connected && <Badge className="bg-green-600/20 text-green-400 text-[10px] border border-green-500/30">SIGINT ACTIVE</Badge>}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSoundEnabled(!soundEnabled)} className="text-slate-400 h-7">
              {soundEnabled ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="max-h-[400px] overflow-y-auto font-mono text-xs space-y-1">
          {liveEvents.length === 0 && <p className="text-slate-600 text-center py-8">SIGINT monitoring active -- no events intercepted</p>}
          {liveEvents.map((e, i) => (
            <div key={i} className={`p-2 rounded border ${severityBorder(e.severity)} bg-slate-900/50 flex items-start gap-2`}>
              <Badge className={`${severityColor(e.severity)} text-[10px] px-1.5 py-0 shrink-0`}>{e.severity || 'LOW'}</Badge>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-red-400 font-bold">{e.threat_name || e.event_type}</span>
                  {e.threat_score && <span className="text-fuchsia-400 text-[10px]">[Score: {e.threat_score}]</span>}
                  {e.country && e.country !== 'Unknown' && <span className="text-slate-500">{e.city}, {e.country}</span>}
                </div>
                <div className="text-slate-500 truncate">{e.ip} {e.isp && e.isp !== 'Unknown' ? `(${e.isp})` : ''}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ATTACKER PROFILE MODAL ====================
const AttackerProfile = ({ ip, token, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/threat-intel/${ip}`, { headers: { Authorization: `Bearer ${token}` } });
        setProfile(res.data);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [ip, token]);

  if (loading) return <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-green-400 font-mono animate-pulse">ANALYZING TARGET...</div></div>;
  if (!profile) return null;

  const p = profile;
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-950 border border-red-500/30 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-red-500/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skull className="w-6 h-6 text-red-500" />
            <div>
              <h2 className="text-lg font-mono font-bold text-red-400">THREAT ACTOR PROFILE</h2>
              <p className="text-xs text-slate-500">NSA Threat Intelligence Report</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${severityColor(p.threat_level)} font-mono`}>{p.threat_level}</Badge>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-900 rounded p-3 border border-slate-800">
              <div className="text-[10px] text-slate-500 uppercase font-mono">IP Address</div>
              <div className="text-lg font-mono text-green-400">{p.ip}</div>
            </div>
            <div className="bg-slate-900 rounded p-3 border border-slate-800">
              <div className="text-[10px] text-slate-500 uppercase font-mono">Device Fingerprint</div>
              <div className="text-lg font-mono text-yellow-400">{p.device_fingerprint}</div>
            </div>
          </div>

          <div className="bg-slate-900 rounded p-3 border border-slate-800">
            <div className="text-[10px] text-slate-500 uppercase font-mono mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location & Network Intelligence</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Address: </span><span className="text-white font-mono">{p.location_summary}</span></div>
              <div><span className="text-slate-500">ISP: </span><span className="text-white font-mono">{p.isp}</span></div>
              <div><span className="text-slate-500">Organization: </span><span className="text-white font-mono">{p.org || 'N/A'}</span></div>
              <div><span className="text-slate-500">AS Number: </span><span className="text-white font-mono">{p.as_number || 'N/A'}</span></div>
              <div><span className="text-slate-500">Reverse DNS: </span><span className="text-white font-mono">{p.reverse_dns || 'N/A'}</span></div>
              <div className="flex gap-3">
                {p.is_proxy && <Badge variant="destructive" className="text-[10px]">PROXY/VPN</Badge>}
                {p.is_hosting && <Badge className="bg-purple-600 text-[10px]">HOSTING</Badge>}
                {p.is_mobile && <Badge className="bg-blue-600 text-[10px]">MOBILE</Badge>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-red-950/50 border border-red-500/30 rounded p-3 text-center">
              <div className="text-2xl font-mono font-bold text-red-400">{p.total_events}</div>
              <div className="text-[10px] text-slate-500 uppercase">Total Events</div>
            </div>
            <div className="bg-red-950/50 border border-red-500/30 rounded p-3 text-center">
              <div className="text-2xl font-mono font-bold text-red-500">{p.critical_events}</div>
              <div className="text-[10px] text-slate-500 uppercase">Critical</div>
            </div>
            <div className="bg-orange-950/50 border border-orange-500/30 rounded p-3 text-center">
              <div className="text-2xl font-mono font-bold text-orange-400">{p.high_events}</div>
              <div className="text-[10px] text-slate-500 uppercase">High</div>
            </div>
          </div>

          <div className="bg-slate-900 rounded p-3 border border-slate-800">
            <div className="text-[10px] text-slate-500 uppercase font-mono mb-2 flex items-center gap-1"><Target className="w-3 h-3" /> Attack Classification</div>
            <div className="space-y-1">
              {Object.entries(p.classified_attacks || {}).sort((a, b) => b[1].count - a[1].count).map(([type, info]) => (
                <div key={type} className="flex items-center justify-between text-sm py-1 border-b border-slate-800 last:border-0">
                  <div className="flex items-center gap-2">
                    <Badge className={`${severityColor(info.severity)} text-[10px] px-1.5 py-0`}>{info.severity}</Badge>
                    <span className="text-white font-mono">{info.name}</span>
                  </div>
                  <span className="text-red-400 font-mono font-bold">{info.count}x</span>
                </div>
              ))}
            </div>
          </div>

          {p.user_agents?.length > 0 && (
            <div className="bg-slate-900 rounded p-3 border border-slate-800">
              <div className="text-[10px] text-slate-500 uppercase font-mono mb-2 flex items-center gap-1"><Fingerprint className="w-3 h-3" /> Known User Agents ({p.user_agents.length})</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {p.user_agents.map((ua, i) => <div key={i} className="text-xs text-slate-400 font-mono truncate">{ua}</div>)}
              </div>
            </div>
          )}

          <div className="flex justify-between text-xs text-slate-500">
            <span>First seen: {p.first_seen ? new Date(p.first_seen).toLocaleString() : 'N/A'}</span>
            <span>Last seen: {p.last_seen ? new Date(p.last_seen).toLocaleString() : 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== TOP THREATS TABLE ====================
const TopThreats = ({ token, onSelectIP }) => {
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchThreats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/top-threats?limit=25`, { headers: { Authorization: `Bearer ${token}` } });
      setThreats(res.data.threats || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchThreats(); }, [token]);

  return (
    <Card className="bg-slate-950 border-slate-800">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-mono flex items-center gap-2"><Skull className="w-4 h-4 text-red-500" /> TOP THREAT ACTORS</CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchThreats} className="text-slate-400 h-7"><RefreshCw className="w-3 h-3" /></Button>
        </div>
      </CardHeader>
      <CardContent className="max-h-[500px] overflow-y-auto">
        {loading ? <p className="text-slate-600 text-center py-8 font-mono">Loading threat data...</p> :
        threats.length === 0 ? <p className="text-slate-600 text-center py-8 font-mono">No threats detected</p> :
        <div className="space-y-1">
          {threats.map((t, i) => (
            <div key={i}
              className={`p-2 rounded border ${severityBorder(t.max_severity)} bg-slate-900/50 cursor-pointer hover:bg-slate-800/80 transition-colors`}
              data-testid={`threat-row-${i}`}
              onClick={() => onSelectIP(t.ip)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={`${severityColor(t.max_severity)} text-[10px] px-1.5 py-0`}>{t.max_severity}</Badge>
                  <span className="font-mono text-sm text-green-400">{t.ip}</span>
                  {t.is_proxy && <Badge variant="outline" className="text-[10px] border-purple-500 text-purple-400">VPN</Badge>}
                </div>
                <span className="text-red-400 font-mono font-bold text-sm">{t.total_events}</span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.city}, {t.country}</span>
                <span className="flex items-center gap-1"><Server className="w-3 h-3" />{t.isp}</span>
              </div>
            </div>
          ))}
        </div>}
      </CardContent>
    </Card>
  );
};

// ==================== BLACKLIST MANAGER ====================
const BlacklistManager = ({ token }) => {
  const [blacklist, setBlacklist] = useState([]);
  const [newIP, setNewIP] = useState('');
  const [reason, setReason] = useState('');

  const fetchBlacklist = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/security/blacklist`, { headers: { Authorization: `Bearer ${token}` } });
      setBlacklist(res.data.blacklist || []);
    } catch {}
  };

  useEffect(() => { fetchBlacklist(); }, [token]);

  const addToBlacklist = async () => {
    if (!newIP.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/security/blacklist`, { ip: newIP.trim(), reason: reason || 'Manual block' }, { headers: { Authorization: `Bearer ${token}` } });
      setNewIP(''); setReason('');
      fetchBlacklist();
      toast.success(`IP ${newIP.trim()} blocked`);
    } catch {}
  };

  const removeFromBlacklist = async (ip) => {
    try {
      await axios.delete(`${API_URL}/api/admin/security/blacklist/${ip}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchBlacklist();
      toast.success(`IP ${ip} unblocked`);
    } catch {}
  };

  return (
    <Card className="bg-slate-950 border-slate-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-mono flex items-center gap-2"><Ban className="w-4 h-4 text-red-500" /> IP BLACKLIST</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-3">
          <Input placeholder="IP Address" value={newIP} onChange={e => setNewIP(e.target.value)} className="bg-slate-900 border-slate-700 font-mono text-sm h-8" data-testid="blacklist-ip-input" />
          <Input placeholder="Reason" value={reason} onChange={e => setReason(e.target.value)} className="bg-slate-900 border-slate-700 text-sm h-8" />
          <Button size="sm" onClick={addToBlacklist} className="bg-red-600 hover:bg-red-700 h-8 shrink-0" data-testid="blacklist-add-btn"><Ban className="w-3 h-3 mr-1" /> Block</Button>
        </div>
        <div className="space-y-1 max-h-[300px] overflow-y-auto">
          {blacklist.length === 0 && <p className="text-slate-600 text-center py-4 text-xs font-mono">No IPs blacklisted</p>}
          {blacklist.map((item, i) => (
            <div key={i} className={`flex items-center justify-between p-2 rounded border text-sm ${item.auto_blocked ? 'bg-red-950/30 border-fuchsia-500/30' : 'bg-slate-900/50 border-red-500/20'}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-red-400">{item.ip}</span>
                  {item.auto_blocked && <Badge className="bg-fuchsia-600 text-[10px]">AUTO</Badge>}
                  <span className="text-slate-500">-- {item.city}, {item.country} ({item.isp})</span>
                </div>
                <div className="text-xs text-slate-600">{item.reason} | by {item.blocked_by} | {item.threat_score ? `Score: ${item.threat_score}` : ''}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeFromBlacklist(item.ip)} className="text-green-400 h-7" data-testid={`unblock-${item.ip}`}>
                <Unlock className="w-3 h-3 mr-1" /> Unblock
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== NSA AUTO-BLOCK INTELLIGENCE ====================
const AutoBlockIntel = ({ token, onSelectIP }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whitelistIP, setWhitelistIP] = useState('');
  const [whitelist, setWhitelist] = useState([]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [statsRes, wlRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/security/auto-block-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/admin/security/whitelist`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setWhitelist(wlRes.data.whitelist || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchStats(); }, [token]);

  const addWhitelist = async () => {
    if (!whitelistIP.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/security/whitelist`, { ip: whitelistIP.trim(), reason: 'Admin whitelist' }, { headers: { Authorization: `Bearer ${token}` } });
      setWhitelistIP('');
      fetchStats();
      toast.success(`IP ${whitelistIP.trim()} whitelisted`);
    } catch {}
  };

  const removeWhitelist = async (ip) => {
    try {
      await axios.delete(`${API_URL}/api/admin/security/whitelist/${ip}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchStats();
      toast.success(`Removed ${ip} from whitelist`);
    } catch {}
  };

  if (loading) return <p className="text-slate-600 text-center py-8 font-mono">Loading intelligence data...</p>;

  return (
    <div className="space-y-4">
      {/* Auto-Block Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-slate-950 border-fuchsia-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <Siren className="w-8 h-8 text-fuchsia-400" />
            <div>
              <div className="text-2xl font-mono font-bold text-white">{stats?.auto_blocked_count || 0}</div>
              <div className="text-[10px] text-slate-500 uppercase font-mono">Auto-Blocked IPs</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-950 border-red-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-red-400" />
            <div>
              <div className="text-2xl font-mono font-bold text-white">{stats?.total_tracked_ips || 0}</div>
              <div className="text-[10px] text-slate-500 uppercase font-mono">Tracked IPs</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-950 border-orange-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <Network className="w-8 h-8 text-orange-400" />
            <div>
              <div className="text-2xl font-mono font-bold text-white">{stats?.dangerous_subnets?.length || 0}</div>
              <div className="text-[10px] text-slate-500 uppercase font-mono">Hostile Subnets</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-950 border-green-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-green-400" />
            <div>
              <div className="text-2xl font-mono font-bold text-white">{stats?.threshold || 100}</div>
              <div className="text-[10px] text-slate-500 uppercase font-mono">Block Threshold</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Live Threat Scores */}
        <Card className="bg-slate-950 border-slate-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-mono flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-fuchsia-400" /> LIVE THREAT SCORES
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchStats} className="text-slate-400 h-7"><RefreshCw className="w-3 h-3" /></Button>
            </div>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            <div className="space-y-1">
              {(stats?.live_threat_scores || []).map((s, i) => (
                <div key={i} className={`flex items-center justify-between p-2 rounded border cursor-pointer hover:bg-slate-800/80 transition-colors ${
                  s.blocked ? 'bg-red-950/30 border-fuchsia-500/30' : s.score >= 70 ? 'bg-orange-950/20 border-orange-500/20' : 'bg-slate-900/50 border-slate-800'
                }`} onClick={() => onSelectIP(s.ip)} data-testid={`score-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-green-400">{s.ip}</span>
                    {s.blocked && <Badge className="bg-fuchsia-600 text-[10px]">BLOCKED</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${s.score >= 100 ? 'bg-fuchsia-500' : s.score >= 70 ? 'bg-red-500' : s.score >= 40 ? 'bg-orange-500' : 'bg-yellow-500'}`}
                        style={{ width: `${Math.min(100, s.score)}%` }} />
                    </div>
                    <span className={`font-mono font-bold text-sm ${s.score >= 100 ? 'text-fuchsia-400' : s.score >= 70 ? 'text-red-400' : 'text-yellow-400'}`}>{s.score}</span>
                  </div>
                </div>
              ))}
              {(!stats?.live_threat_scores || stats.live_threat_scores.length === 0) &&
                <p className="text-slate-600 text-center py-4 text-xs font-mono">No active threat scores</p>}
            </div>
          </CardContent>
        </Card>

        {/* Whitelist + Dangerous Subnets */}
        <div className="space-y-4">
          {/* Whitelist */}
          <Card className="bg-slate-950 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-mono flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-green-400" /> IP WHITELIST (Never Auto-Block)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input placeholder="IP to whitelist" value={whitelistIP} onChange={e => setWhitelistIP(e.target.value)}
                  className="bg-slate-900 border-slate-700 font-mono text-sm h-8" data-testid="whitelist-ip-input" />
                <Button size="sm" onClick={addWhitelist} className="bg-green-600 hover:bg-green-700 h-8 shrink-0" data-testid="whitelist-add-btn">
                  <ShieldCheck className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {whitelist.length === 0 && <p className="text-slate-600 text-center py-3 text-xs font-mono">No whitelisted IPs</p>}
                {whitelist.map((w, i) => (
                  <div key={i} className="flex items-center justify-between p-1.5 bg-green-950/20 rounded border border-green-500/20 text-xs">
                    <span className="font-mono text-green-400">{w.ip}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeWhitelist(w.ip)} className="text-red-400 h-6 text-xs">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Dangerous Subnets */}
          {stats?.dangerous_subnets?.length > 0 && (
            <Card className="bg-slate-950 border-orange-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-mono flex items-center gap-2">
                  <Network className="w-4 h-4 text-orange-400" /> HOSTILE SUBNETS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {stats.dangerous_subnets.map((s, i) => (
                  <div key={i} className="p-2 bg-orange-950/20 rounded border border-orange-500/20 text-xs font-mono">
                    <div className="flex items-center justify-between">
                      <span className="text-orange-400 font-bold">{s.subnet}</span>
                      <Badge className="bg-orange-600 text-[10px]">{s.count} blocked IPs</Badge>
                    </div>
                    <div className="text-slate-500 mt-1 truncate">{s.blocked_ips.join(', ')}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Auto-Blocked IPs */}
          <Card className="bg-slate-950 border-fuchsia-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-mono flex items-center gap-2">
                <Siren className="w-4 h-4 text-fuchsia-400" /> AUTO-BLOCKED IPs ({stats?.auto_blocked_count || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[250px] overflow-y-auto space-y-1">
              {(stats?.auto_blocked || []).length === 0 && <p className="text-slate-600 text-center py-3 text-xs font-mono">No auto-blocked IPs yet</p>}
              {(stats?.auto_blocked || []).map((b, i) => (
                <div key={i} className="p-2 bg-fuchsia-950/20 rounded border border-fuchsia-500/20 text-xs font-mono cursor-pointer hover:bg-fuchsia-950/40"
                  onClick={() => onSelectIP(b.ip)}>
                  <div className="flex items-center justify-between">
                    <span className="text-fuchsia-400 font-bold">{b.ip}</span>
                    <Badge className="bg-fuchsia-600 text-[10px]">Score: {b.threat_score}</Badge>
                  </div>
                  <div className="text-slate-500 mt-0.5">{b.city}, {b.country} ({b.isp}) | {b.blocked_at ? new Date(b.blocked_at).toLocaleString() : ''}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ==================== STATS OVERVIEW ====================
const SecurityStats = ({ token }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/security-stats`, { headers: { Authorization: `Bearer ${token}` } });
        setStats(res.data);
      } catch {}
    };
    fetch();
  }, [token]);

  if (!stats) return null;

  const statCards = [
    { label: 'TOTAL EVENTS', value: stats.total_events || stats.total || 0, icon: Activity, color: 'text-blue-400', border: 'border-blue-500/30' },
    { label: 'LAST 24H', value: stats.events_last_24h || stats.recent_count || 0, icon: Clock, color: 'text-yellow-400', border: 'border-yellow-500/30' },
    { label: 'UNIQUE ATTACKERS', value: stats.top_offending_ips?.length || stats.top_ips?.length || 0, icon: Crosshair, color: 'text-red-400', border: 'border-red-500/30' },
    { label: 'THREAT TYPES', value: stats.event_types?.length || 0, icon: AlertTriangle, color: 'text-orange-400', border: 'border-orange-500/30' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {statCards.map((s, i) => (
        <Card key={i} className={`bg-slate-950 border-slate-800 ${s.border}`}>
          <CardContent className="p-3 flex items-center gap-3">
            <s.icon className={`w-8 h-8 ${s.color}`} />
            <div>
              <div className="text-2xl font-mono font-bold text-white">{s.value.toLocaleString()}</div>
              <div className="text-[10px] text-slate-500 uppercase font-mono">{s.label}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ==================== EVENT LOG TABLE ====================
const EventLog = ({ token, onSelectIP }) => {
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchEvents = async (p = 1) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/security-events?page=${p}&limit=30`, { headers: { Authorization: `Bearer ${token}` } });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch {}
  };

  useEffect(() => { fetchEvents(); }, [token]);

  return (
    <Card className="bg-slate-950 border-slate-800">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-mono flex items-center gap-2"><Eye className="w-4 h-4 text-cyan-400" /> SECURITY EVENT LOG ({total})</CardTitle>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => fetchEvents(page - 1)} className="h-7 text-xs">Prev</Button>
            <span className="text-xs text-slate-500 py-1.5 font-mono">Page {page}</span>
            <Button variant="ghost" size="sm" disabled={events.length < 30} onClick={() => fetchEvents(page + 1)} className="h-7 text-xs">Next</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="max-h-[500px] overflow-y-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-slate-500 border-b border-slate-800">
              <th className="text-left py-1.5 px-1">SEV</th>
              <th className="text-left py-1.5 px-1">THREAT</th>
              <th className="text-left py-1.5 px-1">IP</th>
              <th className="text-left py-1.5 px-1">LOCATION</th>
              <th className="text-left py-1.5 px-1">ISP</th>
              <th className="text-left py-1.5 px-1">SCORE</th>
              <th className="text-left py-1.5 px-1">TIME</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <tr key={i} className="border-b border-slate-900 hover:bg-slate-900/50 cursor-pointer" onClick={() => onSelectIP(e.ip)}>
                <td className="py-1.5 px-1"><Badge className={`${severityColor(e.severity || 'LOW')} text-[9px] px-1 py-0`}>{e.severity || '?'}</Badge></td>
                <td className="py-1.5 px-1 text-white">{e.threat_name || e.event_type}</td>
                <td className="py-1.5 px-1 text-green-400">{e.ip}</td>
                <td className="py-1.5 px-1 text-slate-400">{e.city && e.city !== 'Unknown' ? `${e.city}, ${e.country}` : e.country || '?'}</td>
                <td className="py-1.5 px-1 text-slate-500">{e.isp || '?'}</td>
                <td className="py-1.5 px-1 text-fuchsia-400 font-bold">{e.threat_score || '-'}</td>
                <td className="py-1.5 px-1 text-slate-600">{e.created_at ? new Date(e.created_at).toLocaleTimeString() : '?'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

// ==================== MAIN DASHBOARD ====================
const SecurityDashboard = () => {
  const { token } = useAuth();
  const [selectedIP, setSelectedIP] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'THREAT OVERVIEW', icon: Radar },
    { id: 'autoblock', label: 'AUTO-BLOCK', icon: Siren },
    { id: 'threats', label: 'TOP THREATS', icon: Skull },
    { id: 'events', label: 'EVENT LOG', icon: Eye },
    { id: 'blacklist', label: 'BLACKLIST', icon: Ban },
  ];

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="security-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-gradient-to-br from-red-600/30 to-fuchsia-600/30 border border-red-500/30 flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-mono font-bold text-white tracking-wider" data-testid="security-title">NSA SECURITY OPERATIONS CENTER</h1>
              <p className="text-xs text-slate-500 font-mono">Military-Grade WAF + Auto-Block Intelligence + Threat Scoring</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-fuchsia-600/20 text-fuchsia-400 border border-fuchsia-500/30 font-mono text-[10px]">AUTO-BLOCK ACTIVE</Badge>
            <Badge className="bg-green-600/20 text-green-400 border border-green-500/30 font-mono text-[10px]">SIGINT ONLINE</Badge>
          </div>
        </div>

        {/* Stats */}
        <SecurityStats token={token} />

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-800 pb-0 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`security-tab-${tab.id}`}
              className={`px-3 py-2 text-xs font-mono flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-red-500 text-red-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}>
              <tab.icon className="w-3 h-3" />{tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LiveFeed token={token} />
            <TopThreats token={token} onSelectIP={setSelectedIP} />
          </div>
        )}

        {activeTab === 'autoblock' && <AutoBlockIntel token={token} onSelectIP={setSelectedIP} />}
        {activeTab === 'threats' && <TopThreats token={token} onSelectIP={setSelectedIP} />}
        {activeTab === 'events' && <EventLog token={token} onSelectIP={setSelectedIP} />}
        {activeTab === 'blacklist' && <BlacklistManager token={token} />}

        {/* Attacker Profile Modal */}
        {selectedIP && <AttackerProfile ip={selectedIP} token={token} onClose={() => setSelectedIP(null)} />}
      </div>
    </AdminLayout>
  );
};

export default SecurityDashboard;
